// red. MemberCard — Mitgliedskarte (Brand-Artefakt, eigene Palette unabhängig
// vom App-Theme). Basis-Handoff: README/member-card. Fonts auf App-Tokens
// gemappt (F.brand Wortmarke, F.family Tier, F.num Nummer) — Outfit/Space Grotesk
// werden nicht geladen, daher bewusst die vorhandenen App-Schriften.
// Verwendung: <MemberCard tier="vip" memberSince="2026" no="0007" width={300} />
// FloatingMemberCard: sanfter Float-Loop via Framer Motion (kein Maus-Tilt, kein Three.js).
import React from 'react';
import { motion } from 'framer-motion';
import { F } from '../../styles/theme';

export const MC_COLORS = {
  ink: '#1B1B1E',
  coral: '#E4544A',
  cream: '#EDE7DB',
};

export const MC_THEME = {
  free: {
    bg: 'linear-gradient(155deg, #FDFCFA, #F4F1EA)',
    shadow: '0 10px 28px rgba(60,30,20,0.10), inset 0 0 0 1px rgba(27,27,30,0.06)',
    emboss: { cells: 'rgba(27,27,30,0.025)', dot: 'rgba(27,27,30,0.045)', plus: 'rgba(27,27,30,0.09)' },
    logoCells: MC_COLORS.ink,
    word: MC_COLORS.ink,
    tier: '#A89F90',
    foot: '#B4AC9E',
    no: '#C4BBAA',
  },
  premium: {
    bg: 'linear-gradient(155deg, #F6F0E6, #EDE5D6)',
    shadow: '0 12px 32px rgba(60,30,20,0.14), inset 0 0 0 1px rgba(27,27,30,0.05)',
    emboss: { cells: 'rgba(27,27,30,0.035)', dot: 'rgba(27,27,30,0.06)', plus: 'rgba(27,27,30,0.12)' },
    logoCells: MC_COLORS.ink,
    word: MC_COLORS.ink,
    tier: '#8A8378',
    foot: '#A89F90',
    no: '#B9AFA2',
  },
  vip: {
    bg: 'linear-gradient(155deg, #1A1A1E, #0F0F12)',
    shadow: '0 14px 36px rgba(15,10,8,0.4), inset 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 0 rgba(255,255,255,0.07)',
    emboss: { cells: 'rgba(255,255,255,0.025)', dot: 'rgba(255,255,255,0.05)', plus: 'rgba(255,255,255,0.10)' },
    logoCells: MC_COLORS.cream,
    word: '#FDFCFA',
    tier: '#7E776B',
    foot: '#6B655B',
    no: '#56514A',
  },
};

/** Logo-Grid (Bildmarke). cells/dot/plus überschreibbar für die Prägung. */
export function MCGridMark({ size = 20, cells = MC_COLORS.ink, dot = MC_COLORS.coral, plus = '#FFFFFF', style }) {
  return (
    <svg width={size} height={size} viewBox="14 14 132 132" style={{ display: 'block', ...style }} aria-hidden="true">
      <rect x="18" y="18" width="58" height="58" rx="17" fill={cells} />
      <rect x="84" y="18" width="58" height="58" rx="17" fill={cells} />
      <rect x="18" y="84" width="58" height="58" rx="17" fill={cells} />
      <rect x="84" y="84" width="58" height="58" rx="17" fill={dot} />
      <rect x="97" y="109.5" width="32" height="7" rx="3.5" fill={plus} />
      <rect x="109.5" y="97" width="7" height="32" rx="3.5" fill={plus} />
    </svg>
  );
}

/**
 * Member-Card. tier: 'free' | 'premium' | 'vip'.
 * width skaliert die gesamte Karte proportional (Basis 252×158).
 */
export function MemberCard({ tier = 'free', memberSince = '2026', no = '0001', width = 252, style }) {
  const th = MC_THEME[tier] || MC_THEME.free;
  const f = width / 252;                  // Skalierungsfaktor
  const s = (v) => v * f;
  return (
    <div style={{
      position: 'relative', width: s(252), height: s(158),
      borderRadius: s(16), overflow: 'hidden',
      background: th.bg, boxShadow: th.shadow,
      fontFamily: F.family, ...style,
    }}>
      {/* Ton-in-Ton-Prägung, überhängend oben rechts */}
      <MCGridMark size={s(132)} cells={th.emboss.cells} dot={th.emboss.dot} plus={th.emboss.plus}
        style={{ position: 'absolute', right: s(-28), top: s(-28) }} />

      {/* Logo oben links */}
      <MCGridMark size={s(20)} cells={th.logoCells}
        style={{ position: 'absolute', left: s(20), top: s(18) }} />

      {/* Wortmarke + Tier auf gemeinsamer Baseline */}
      <div style={{ position: 'absolute', left: s(20), bottom: s(40), display: 'flex', alignItems: 'baseline', gap: s(10) }}>
        <span style={{
          display: 'inline-flex', alignItems: 'baseline', lineHeight: 1,
          fontFamily: F.brand, fontWeight: 700, fontSize: s(21), letterSpacing: '-0.03em', color: th.word,
        }}>
          red
          <span style={{
            display: 'inline-block', width: s(3.6), height: s(3.6), borderRadius: '30%',
            background: MC_COLORS.coral, marginLeft: s(1.5),
          }} />
        </span>
        <span style={{
          display: 'inline-flex', alignItems: 'baseline', lineHeight: 1,
          fontFamily: F.brand, fontWeight: 500, fontSize: s(13.5), letterSpacing: '0.01em',
          textTransform: 'lowercase', color: th.tier,
        }}>
          {tier}
          <span style={{
            display: 'inline-block', width: s(2.3), height: s(2.3), borderRadius: '22%',
            background: MC_COLORS.coral, marginLeft: s(1.2),
          }} />
        </span>
      </div>

      {/* Fußzeile */}
      <div style={{ position: 'absolute', left: s(20), bottom: s(18), fontWeight: 500, fontSize: s(10.5), letterSpacing: '0.04em', color: th.foot }}>
        MITGLIED SEIT {memberSince}
      </div>
      <div style={{ position: 'absolute', right: s(20), bottom: s(18), fontFamily: F.num, fontWeight: 500, fontSize: s(10.5), letterSpacing: '0.1em', color: th.no }}>
        Nº {no}
      </div>
    </div>
  );
}

/**
 * FloatingMemberCard — MemberCard mit sanftem Float-Loop.
 * Kein Maus-Tilt, kein Three.js — nur ruhiges Schweben via Framer Motion.
 */
export function FloatingMemberCard({ tier = 'free', memberSince = '2026', no = '0001', width = 300, style }) {
  return (
    <motion.div
      animate={{ y: [0, -10, 0], rotate: [-0.4, 0.4, -0.4] }}
      transition={{ duration: 5.5, ease: 'easeInOut', repeat: Infinity, repeatType: 'mirror' }}
      style={style}
    >
      <MemberCard tier={tier} memberSince={memberSince} no={no} width={width} />
    </motion.div>
  );
}

export default MemberCard;
